import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API_URL } from "@/env";
import { getCookie } from "@/lib/cookies";
import { showCustomToast } from "../CustomToast";
import useUserData from "@/hooks/useUserData";
import { useWorkspaceStore } from "@/hooks/useWorkspace";

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const { user, setUser } = useUserData();
  const { addWorkspace, setCurrentWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();

  useEffect(() => {
    const invitationId = searchParams.get("invitationId");

    if (!invitationId) {
      showCustomToast("Invalid invitation", "exclamation");
      return;
    }

    acceptInvitation(invitationId);
  }, [searchParams]);

  const acceptInvitation = async (invitationId: string) => {
    try {
      const token = getCookie("token");

      const response = await fetch(
        `${API_URL}/workspaces/invitations/${invitationId}/accept`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to accept invitation");
      }

      const data = await response.json();

      if (data.workspace) {
        addWorkspace(data.workspace);
        setCurrentWorkspace(data.workspace);
      }

      // Refetch user so App.tsx effect syncs the full workspaces list
      const userRes = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const updatedUser = await userRes.json();
      if (updatedUser) {
        setUser(updatedUser);
      }

      showCustomToast("Joined workspace, redirecting...", "check");

      setTimeout(() => {
        navigate(`/dashboard/${data.workspace._id}`);
      }, 1000);
    } catch (error: any) {
      showCustomToast("Failed to accept invitation", "exclamation");
      if (user) {
        navigate(`/dashboard`);
      } else {
        navigate("/auth");
      }
    }
  };

  return null;
}
